import { useState } from "react";
import { StarRating } from "@/components/reviews/StarRating";
import { formatDate } from "@/lib/format";
import type { TherapistReview } from "@/types";
import { REVIEW_REJECTION_REASON_MAX } from "@/schemas/reviews";
import { REVIEW_TEXT_MAX } from "@/lib/review-limits";

type Props = {
  items: TherapistReview[];
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string, reason: string) => Promise<void>;
  onHide: (id: string) => Promise<void>;
  onEdit: (id: string, patch: { reviewText?: string; rating?: number }) => Promise<void>;
};

export function ReviewsModerationTable({ items, onApprove, onReject, onHide, onEdit }: Props) {
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [busy, setBusy] = useState(false);

  // Callers toast the error and rethrow; on failure the form stays open.
  async function run(action: () => Promise<void>, onDone: () => void) {
    setBusy(true);
    try {
      await action();
      onDone();
    } catch {
      // Error already shown by the caller.
    } finally {
      setBusy(false);
    }
  }

  return (
    <ul className="space-y-3">
      {items.map((r) => (
        <li key={r.id} className="rounded-lg border border-neutral-200 p-4">
          <div className="flex items-center justify-between">
            <StarRating value={r.rating} readOnly size="sm" />
            <span className="text-xs text-neutral-500">{formatDate(r.createdAt)}</span>
          </div>
          <p className="mt-2 whitespace-pre-wrap text-sm">{r.reviewText}</p>
          {r.adminEditedAt ? <div className="mt-1 text-xs text-amber-700">Uredio admin {formatDate(r.adminEditedAt)}</div> : null}
          {r.rejectionReason ? <div className="mt-1 text-xs text-red-700">Razlog odbijanja: {r.rejectionReason}</div> : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {r.status === "pending" ? (
              <>
                <button className="rounded-md bg-emerald-600 px-3 py-1 text-sm text-white" disabled={busy} onClick={() => run(() => onApprove(r.id), () => {})}>Odobri</button>
                <button className="rounded-md bg-red-600 px-3 py-1 text-sm text-white" onClick={() => setRejectingId(r.id)}>Odbij</button>
              </>
            ) : null}
            {r.status === "approved" ? (
              <button className="rounded-md bg-amber-600 px-3 py-1 text-sm text-white" disabled={busy} onClick={() => run(() => onHide(r.id), () => {})}>Sakrij</button>
            ) : null}
            <button className="rounded-md border px-3 py-1 text-sm" onClick={() => { setEditingId(r.id); setEditText(r.reviewText); }}>Uredi tekst</button>
          </div>
          {rejectingId === r.id ? (
            <div className="mt-3">
              <input className="w-full rounded-md border border-neutral-300 p-2 text-sm" placeholder="Razlog odbijanja" maxLength={REVIEW_REJECTION_REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value)} />
              <div className="mt-2 flex gap-2">
                <button className="rounded-md bg-red-600 px-3 py-1 text-sm text-white disabled:opacity-50" disabled={busy || reason.trim() === ""} onClick={() => run(() => onReject(r.id, reason.trim()), () => { setRejectingId(null); setReason(""); })}>Potvrdi odbijanje</button>
                <button className="rounded-md border px-3 py-1 text-sm" onClick={() => { setRejectingId(null); setReason(""); }}>Otkaži</button>
              </div>
            </div>
          ) : null}
          {editingId === r.id ? (
            <div className="mt-3">
              <textarea className="w-full rounded-md border border-neutral-300 p-2 text-sm" rows={4} maxLength={REVIEW_TEXT_MAX} value={editText} onChange={(e) => setEditText(e.target.value)} />
              <div className="mt-2 flex gap-2">
                <button className="rounded-md bg-neutral-900 px-3 py-1 text-sm text-white disabled:opacity-50" disabled={busy || editText.trim() === ""} onClick={() => run(() => onEdit(r.id, { reviewText: editText }), () => setEditingId(null))}>Sačuvaj</button>
                <button className="rounded-md border px-3 py-1 text-sm" onClick={() => setEditingId(null)}>Otkaži</button>
              </div>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
